// evaluation 결과 표와 상세가 함께 쓰는 표시 값이다. 컴포넌트는 이 순수 포맷터를 써서 로직을
// 줄인다.
import type { TrainingEvalRunSummaryView } from "@/api/trainingEval";
import { sessionSimpleStatus } from "@/modules/training/public";
import { formatClock, formatDateTime, parseUtcMs } from "@/utils/datetime";

const EMPTY_CELL = "—";

// eval run 이 아직 할 일이 남아 있을 때 보고하는 raw status 목록이다. eval-run 어휘
// (`docs/api.md` Training)이고, `modules/training` 세션 상태 계약의 세션 어휘보다 좁은 부분 집합이다.
// 이 값들은 `waiting` 과 `in_progress` 표시 카테고리로 합쳐진다. 상세 폴링과 사이드바의 실행 중
// run 수가 모두 이 목록을 읽는다. 그래서 새 진행 상태는 여기에 한 번만 추가한다.
export const LIVE_EVAL_RUN_STATUSES: readonly string[] = [
  "pending",
  "queued",
  "dispatched",
  "running",
];

export function isLiveEvalStatus(status: string): boolean {
  return LIVE_EVAL_RUN_STATUSES.includes(status);
}

// eval 결과 표, 상태 필터 옵션, run 상세 창 헤더가 쓰는 상태 라벨이다. raw status 를 표시
// 카테고리로 합치는 일(별칭, 대소문자)과 카테고리 이름은 `sessionSimpleStatus` 가 소유한다.
// 그래서 eval 화면과 training 화면이 같은 어휘를 쓴다.
export function evalRunStatusLabel(rawStatus: string): string {
  return sessionSimpleStatus(rawStatus).label;
}

export function formatEvalDateTime(iso: string | null): string {
  return formatDateTime(parseUtcMs(iso));
}

/**
 * run 소요 시간을 포맷한다. 끝나지 않은 run 은 시작 이후 경과 시간을 쓴다.
 *
 * Args:
 *   startedAt: 시작 시각.
 *   finishedAt: 종료 시각. 실행 중인 run 이면 null.
 *   nowMs: 현재 시각. 테스트에서 주입할 수 있다.
 *
 * Returns:
 *   `HH:MM:SS` 형식이다. 예: `"00:00:42"`, `"01:05:00"`. 하루 이상인 run 은 시간이 두 자리를
 *   넘는다.
 */
export function formatEvalDuration(
  startedAt: string | null,
  finishedAt: string | null,
  nowMs: number = Date.now(),
): string {
  const start = parseUtcMs(startedAt);
  if (start === null) {
    return EMPTY_CELL;
  }
  const end = parseUtcMs(finishedAt) ?? nowMs;
  return formatClock(end - start);
}

/**
 * backend ETA 를 고정 폭 시계 형식으로 포맷한다. 소요 시간 열, 그리고 run 상세가 옆에 표시하는
 * 경과 시간과 형식이 같다.
 *
 * Args:
 *   seconds: progress 엔드포인트가 보고한 남은 초.
 *
 * Returns:
 *   `HH:MM:SS`. 표시할 값이 없으면 null 이다. 이때 호출부는 빈 구간을 쓰지 않고 ` · eta …`
 *   구간 전체를 뺀다. 음수와 유한하지 않은 값은 값 없음으로 처리한다. `formatClock` 은 그 값을
 *   `00:00:00` 으로 맞춘다. 그러면 "곧 끝남" 으로 읽히고 잘못된 값이 보이지 않는다.
 */
export function formatEvalEta(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  return formatClock(seconds * 1000);
}

export function orEmptyCell(value: string | null): string {
  return value !== null && value !== "" ? value : EMPTY_CELL;
}

/**
 * 산출 세션의 표시 이름이다. 이름이 없으면 id 를 줄여 쓴다.
 *
 * Args:
 *   run: eval-run 목록 행.
 *
 * Returns:
 *   세션 이름, `sess-` 단축 id, 또는 둘 다 없으면 em dash.
 */
export function evalRunSessionLabel(run: TrainingEvalRunSummaryView): string {
  if (run.source_session_name !== null && run.source_session_name !== "") {
    return run.source_session_name;
  }
  return run.source_session_id !== null ? `sess-${run.source_session_id.slice(0, 8)}` : EMPTY_CELL;
}

/**
 * 평가한 정책의 표시 이름이다.
 *
 * 목록 응답에 정책 이름이 없는 run 이 있다. 그때는 세션 이름으로 대체한다. 어느 학습에서 나온
 * run 인지 보여주는 것이 이름이 없는 것보다 낫다. 결과 표와 비교 뷰어가 같은 우선순위를 써야
 * 한 run 이 두 화면에서 다른 이름으로 불리지 않는다.
 *
 * Args:
 *   run: eval-run 목록 행.
 *
 * Returns:
 *   정책 이름, 세션 표시 이름, 또는 둘 다 없으면 em dash.
 */
export function evalRunPolicyLabel(run: TrainingEvalRunSummaryView): string {
  if (run.policy_name !== null && run.policy_name !== "") {
    return run.policy_name;
  }
  return evalRunSessionLabel(run);
}

// 세션 이름을 먼저 쓰고, 없으면 짧은 run id 를 쓴다. 같은 세션의 run 여러 개를 구분하려고
// scenario 를 뒤에 붙인다. 이 이름 규칙은 상세 팝업 헤더가 소유한다.
export function evalRunDetailTitle(run: TrainingEvalRunSummaryView): string {
  const session = run.source_session_name ?? `eval-${run.id.slice(0, 8)}`;
  return run.scenario !== null ? `${session} · ${run.scenario}` : session;
}
